import type { CustomNode, NodeStruct } from '@/types/node-types';
import type { CustomEdge } from '@/types/edge-types';
import {
  getBranchLabels,
  getConditionalBranchLabel,
  isBranchingStruct,
  isConditionalEdge,
} from './functional-control-flow';
import type { LoopRuleProblem } from './repeat-loop';

/**
 * Phase 8d: a Switch or If may sit inside a Repeat Until loop or a For-each
 * body only when every branch rejoins before the region's last node (the
 * judge, or the step whose output is collected). Mirrors the prepare check
 * (`repeat_branch_does_not_rejoin` / `map_branch_does_not_rejoin`,
 * `repeat_judge_on_branch`) so the editor says it before Run does.
 *
 * Inside a region, a branch target outside the region can never rejoin
 * (region = every node on a path from the first node to the last), and a
 * branch label with no edge ends the item there; either way the last node
 * would be skipped on that branch.
 */

export type RegionKind = 'repeat' | 'map';

export const REJOIN_HINT: Record<RegionKind, string> = {
  repeat: 'Both branches must rejoin before the judge; add a Union / Collect.',
  map: 'Both branches must rejoin before the collected step; add a Union / Collect.',
};

const edgeSource = (edge: CustomEdge): string =>
  edge.source_node_id || edge.source;
const edgeTarget = (edge: CustomEdge): string =>
  edge.target_node_id || edge.target;

const struct = (node: CustomNode | undefined): NodeStruct | undefined =>
  node?.data?.struct;

export const isGatingNode = (node: CustomNode | undefined): boolean =>
  isBranchingStruct(struct(node));

/** Edges a Switch / If inside the region sends out (the branches), for skipping the generic "leaves the region" rule. */
export const branchEdgesLeavingRegion = (
  edges: readonly CustomEdge[],
  regionIds: ReadonlySet<string>,
  byId: ReadonlyMap<string, CustomNode>,
): Set<string> =>
  new Set(
    edges
      .filter(
        (edge) =>
          isConditionalEdge(edge) &&
          regionIds.has(edgeSource(edge)) &&
          !regionIds.has(edgeTarget(edge)) &&
          isGatingNode(byId.get(edgeSource(edge))),
      )
      .map((edge) => edge.id),
  );

export const checkBranchesRejoin = (params: {
  kind: RegionKind;
  nodes: readonly CustomNode[];
  edges: readonly CustomEdge[];
  regionIds: readonly string[];
  entryId: string | null;
  exitId: string | null;
  name: (id: string | null) => string;
}): LoopRuleProblem[] => {
  const { kind, nodes, edges, entryId, exitId, name } = params;
  const region = new Set(params.regionIds);
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const problems: LoopRuleProblem[] = [];
  const hint = REJOIN_HINT[kind];
  const exitWord = kind === 'repeat' ? 'the judge' : name(exitId);

  for (const id of params.regionIds) {
    const node = byId.get(id);
    const s = struct(node);
    if (!s || !isBranchingStruct(s)) continue;
    const kindName = s.display_name || s.node_id;

    if (id === entryId) {
      problems.push({
        code: `${kind}_region_entry_gating`,
        severity: 'error',
        message: `${name(id)} (${kindName}) cannot be the first step of the ${
          kind === 'repeat' ? 'loop' : 'For each body'
        }.`,
        hint:
          kind === 'repeat'
            ? 'Start the loop with the step that takes the feedback, then branch after it.'
            : 'Start the body with a step that takes the item (for example a Calculate), then branch after it.',
        nodeIds: [id],
      });
    }

    const branches = edges.filter(
      (edge) => isConditionalEdge(edge) && edgeSource(edge) === id,
    );
    for (const edge of branches) {
      const target = edgeTarget(edge);
      const label = getConditionalBranchLabel(edge) ?? 'a';
      if (target === exitId) {
        problems.push({
          code:
            kind === 'repeat' ? 'repeat_judge_on_branch' : 'map_exit_on_branch',
          severity: 'error',
          message: `${
            kind === 'repeat' ? `The judge ${name(target)}` : name(target)
          } is on the «${label}» branch of ${name(id)}, so another branch would skip it.`,
          hint,
          nodeIds: [id, target],
        });
      } else if (!region.has(target)) {
        problems.push({
          code: `${kind}_branch_does_not_rejoin`,
          severity: 'error',
          message: `The «${label}» branch of ${name(id)} goes to ${name(
            target,
          )} and never reaches ${exitWord}.`,
          hint,
          nodeIds: [id, target],
        });
      }
    }

    const wired = new Set(
      branches
        .map((edge) => getConditionalBranchLabel(edge))
        .filter((label): label is string => Boolean(label)),
    );
    const unwired = getBranchLabels(s).filter((label) => !wired.has(label));
    if (unwired.length && branches.length) {
      problems.push({
        code: `${kind}_branch_does_not_rejoin`,
        severity: 'error',
        message: `The ${unwired.map((label) => `«${label}»`).join(', ')} ${
          unwired.length === 1 ? 'branch' : 'branches'
        } of ${name(id)} ${
          unwired.length === 1 ? 'is' : 'are'
        } not connected, so ${exitWord} would be skipped when ${
          unwired.length === 1 ? 'it is' : 'one is'
        } taken.`,
        hint,
        nodeIds: [id],
      });
    }
  }
  return problems;
};
